import { useGetAco2Tokens } from '@api/get-mint-history';
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';
import { Title } from '@components/v2/ui';
import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import {
	ActionIcon,
	Badge,
	Box,
	Flex,
	Group,
	Image,
	Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconFilter } from '@tabler/icons-react';
import {
	DataTable,
	uniqBy,
	type DataTableSortStatus,
} from 'mantine-datatable';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, QueryParams } from './Filter/Filter';
import classes from './MintHistoryList.module.css';
import { TMintHistoryByProject } from './type';

export const MintHistoryList = ({
	projectId,
}: {
	projectId: string;
}) => {
	const { checkRole } = useAuthorization();
	const { t } = useTranslation();

	const [sortStatus, setSortStatus] = useState<
		DataTableSortStatus<TMintHistoryByProject>
	>({
		columnAccessor: 'createdAt',
		direction: 'asc',
	});

	const [queryParams, setQueryParams] =
		useState<QueryParams>({});

	const { data, isPending } = useGetAco2Tokens({
		params: {
			page: 1,
			pageSize: 0,
			project: projectId,
			query: queryParams,
			sort:
				sortStatus.direction === 'asc'
					? sortStatus.columnAccessor
					: `-${sortStatus.columnAccessor}`,
		},
		queryConfig: {
			enabled: checkRole([
				ROLES.SUPERADMIN,
				ROLES.ADMIN,
				ROLES.STAFF,
			]),
		},
	});

	const [records, setRecords] = useState(
		data?.rows ?? []
	);
	const [selectedRecords, setSelectedRecords] = useState<
		TMintHistoryByProject[]
	>([]);
	const [allRecordsSelected, setAllRecordsSelected] =
		useState(false);
	const [unselectedRecords, setUnselectedRecords] =
		useState<TMintHistoryByProject[]>([]);

	const handleSelectedRecordsChange = (
		newSelectedRecords: TMintHistoryByProject[]
	) => {
		if (allRecordsSelected) {
			const recordsToUnselect = records.filter(
				(record) => !newSelectedRecords.includes(record)
			);
			setUnselectedRecords(
				uniqBy(
					[...unselectedRecords, ...recordsToUnselect],
					(r) => r.id
				).filter((r) => !newSelectedRecords.includes(r))
			);
		} else {
			setSelectedRecords(newSelectedRecords);
		}
	};

	const [
		openedFilters,
		{ open: openFilters, close: closeFilters },
	] = useDisclosure(false);

	const [filtersNbr, setFiltersNbr] = useState(0);

	const handleemptystate = () => {
		return (
			<Box className={classes.containerTextsNoRecords}>
				<Image
					src={NoRecordsIcon}
					className={classes.iconNoRecords}
				/>
				<Text className={classes.titleNoRecords}>
					{t('global.datatable.noResultsFound')}
				</Text>
				<Text className={classes.descriptionNoRecords}>
					{t(
						'global.datatable.noResultsFoundDescription'
					)}
				</Text>
			</Box>
		);
	};

	return (
		<Box>
			<Filter
				opened={openedFilters}
				onClose={closeFilters}
				title={t('filters.titles.projects')}
				onApplyFilters={(filters) => {
					setQueryParams(filters);
					setFiltersNbr(Object.keys(filters).length);
				}}
				value={queryParams}
			/>
			<Flex
				justify={'space-between'}
				align={'center'}
				mb="24px"
			>
				<Title
					order={5}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{t('sidebar.mintHistory')}
				</Title>
				<Group gap={'var(--mantine-spacing-xs)'}>
					<div
						style={{
							position: 'relative',
						}}
					>
						{filtersNbr > 0 && (
							<Badge
								style={{
									position: 'absolute',
									zIndex: '3',
									right: '-3px',
									fontFamily: 'InterDisplay-Regular',
								}}
								color="var(--mantine-color-igluRed-9)"
								size="xs"
								circle
							>
								{filtersNbr}
							</Badge>
						)}
						<ActionIcon
							variant="outline"
							size="48px"
							radius="xl"
							color={'var(--mantine-color-igluBorder-9)'}
							data-testid="bell-icon-button"
							onClick={openFilters}
							bg={
								filtersNbr
									? 'var(--mantine-color-igluPrimary-9)'
									: 'transparent'
							}
						>
							<IconFilter
								color={
									filtersNbr
										? 'var(--mantine-color-igluTextButton-9)'
										: 'var(--mantine-color-igluPrimary-9)'
								}
								radius="xl"
								size={24}
								stroke={1.5}
							/>
						</ActionIcon>
					</div>
				</Group>
			</Flex>
			<DataTable<TMintHistoryByProject>
				height="calc(100vh - 530px)"
				records={data?.rows ?? []}
				selectedRecords={selectedRecords}
				onSelectedRecordsChange={
					handleSelectedRecordsChange
				}
				sortStatus={sortStatus}
				onSortStatusChange={setSortStatus}
				columns={[
					{
						accessor: 'createdAt',
						title: t('global.transactionDate'),
						sortable: true,
					},
					{ accessor: 'startDate', sortable: true },
					{ accessor: 'endDate', sortable: true },
					{ accessor: 'quantityTco2e', sortable: true },
				]}
				classNames={classes}
				fetching={isPending}
				emptyState={handleemptystate()}
			/>
		</Box>
	);
};
